import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import { useLenis } from "@/lib/useLenis";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Solutions from "@/pages/Solutions";
import Offers from "@/pages/Offers";
import Maintenance from "@/pages/Maintenance";
import Services from "@/pages/Services";
import Apps from "@/pages/Apps";
import Contact from "@/pages/Contact";
import Support from "@/pages/Support";
import Download from "@/pages/Download";
import Faq from "@/pages/Faq";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";

function ScrollManager() {
  const { pathname } = useLocation();
  const { lang } = useLanguage();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, lang]);
  return null;
}

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: false }; }
  static getDerivedStateFromError() { return { err: true }; }
  render() {
    if (this.state.err) {
      return (
        <div className="min-h-screen grid place-items-center bg-brand-paper" data-testid="error-boundary">
          <div className="text-center">
            <p className="font-mono text-xs tracking-widest text-brand-blue">SIGNAL LOST</p>
            <p className="mt-3 font-display text-2xl font-bold text-brand-ink">Something went wrong.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function PreviewBanner() {
  const { preview, exitPreview } = useLanguage();
  if (!preview) return null;
  return (
    <div className="fixed bottom-4 start-1/2 z-[90] flex -translate-x-1/2 items-center gap-3 rounded-full bg-brand-amber px-5 py-2.5 text-xs font-bold text-brand-ink shadow-2xl rtl:translate-x-1/2" dir="rtl" data-testid="preview-banner">
      وضع المعاينة — تعرض هذه الصفحة المسودة غير المنشورة
      <button onClick={exitPreview} data-testid="preview-exit-btn" className="rounded-full bg-brand-ink px-3 py-1 text-white">إنهاء المعاينة</button>
    </div>
  );
}

function ContentGate({ children }) {
  const { ready } = useLanguage();
  if (!ready) return <div className="min-h-screen bg-brand-paper" data-testid="content-loading" />;
  return children;
}

function Site() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  return (
    <LanguageProvider>
      <ContentGate>
      <ScrollManager />
      {!isAdmin && <Navbar />}
      {!isAdmin && <PreviewBanner />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/offers" element={<Offers />} />
          <Route path="/maintenance" element={<Maintenance />} />
          <Route path="/services" element={<Services />} />
          <Route path="/apps" element={<Apps />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/support" element={<Support />} />
          <Route path="/download" element={<Download />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/:section" element={<Admin />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <FloatingActions />}
      </ContentGate>
    </LanguageProvider>
  );
}

export default function App() {
  useLenis();
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Site />
        <Toaster position="top-center" richColors closeButton />
      </ErrorBoundary>
    </BrowserRouter>
  );
}
